import { getDispatch, getState } from '../store';
import { updateAudioAction } from '../store/models/actions';
import { setSnackbarPanelAction } from '../store/general/actions';
import { bumpBookReloadAction, setBookCreatingAction, setBookHostAction, setBookRegeneratingAction, setBookSettingsAction } from '../store/books/actions';
import { getBookHost, getBookSettings, isBookCreating, isBookHostFailed } from '../store/books/selectors';
import { AudioRest } from '../../common/rest/audio/audioRest';
import { EndlessBookRest, TBookSettings, TBookVoices, TEndlessBookToken } from '../../common/rest/endlessBook/endlessBookRest';
import { MyStuffUtils } from '../../common/utils/myStuffUtils';
import { IBook, IBookReaderError } from '../../common/endlessBook/embed';
import { Navigator } from './navigator';
import { RecordingsEffects } from './recordingsEffects';
import { EAudioPageTab } from '../components/call/AudioPage/components/types';

// Refetched this long before it actually expires, so a request never goes out with a dying token.
const TOKEN_MARGIN_SEC = 60;

export class BookEffects {

	private static token: TEndlessBookToken | null = null;
	private static tokenRequest: Promise<TEndlessBookToken> | null = null;
	// books already remounted once after a 401
	private static retriedAuth = new Set<number>();

	// Bearer for endless-book, cached until shortly before exp; concurrent callers share one fetch.
	public static async getToken(): Promise<string> {
		const cached = BookEffects.token;
		if (cached && cached.exp - TOKEN_MARGIN_SEC > Date.now() / 1000) return cached.token;
		if (!BookEffects.tokenRequest) {
			BookEffects.tokenRequest = EndlessBookRest.getToken()
				.then((t) => {
					if (!t?.token) throw new Error('no endless-book token');
					BookEffects.token = t;
					if (getBookHost(getState()) !== t.bookHost) getDispatch()(setBookHostAction(t.bookHost));
					return t;
				})
				.finally(() => { BookEffects.tokenRequest = null; });
		}
		return (await BookEffects.tokenRequest).token;
	}

	// The reader needs bookHost before it can mount; it arrives with the first token.
	public static async loadBookHost(): Promise<void> {
		const dispatch = getDispatch();
		if (getBookHost(getState())) return;
		if (isBookHostFailed(getState())) dispatch(setBookHostAction(null));
		try {
			await BookEffects.getToken();
		} catch (e) {
			console.error('BookEffects.loadBookHost: token fetch failed', e);
			dispatch(setBookHostAction(null, true));
		}
	}

	// Creates the lesson's book (page 1 included, so it takes a while) and shows it on the Book tab.
	public static async createBook(audioId: number, navigate = true): Promise<void> {
		const dispatch = getDispatch();
		if (isBookCreating(getState(), audioId)) return;
		const groupId = RecordingsEffects.findGroupId(audioId);
		if (navigate && groupId) Navigator.openAudio(groupId, audioId, EAudioPageTab.BOOK);

		dispatch(setBookCreatingAction(audioId, true));
		try {
			const res = await AudioRest.createBook(audioId, MyStuffUtils.getBookLangs(audioId));
			if (!res?.bookId) throw new Error('no bookId');
			if (groupId) dispatch(updateAudioAction(audioId, groupId, { bookId: res.bookId }));
		} catch (e) {
			console.error('BookEffects.createBook failed', e);
			dispatch(setSnackbarPanelAction(true, 'Failed to create a book'));
		} finally {
			dispatch(setBookCreatingAction(audioId, false));
		}
	}

	public static openBook(audioId: number): void {
		RecordingsEffects.openAudioTab(audioId, EAudioPageTab.BOOK);
	}

	// null on failure — the voice section shows its own error.
	public static async loadVoices(): Promise<TBookVoices | null> {
		try {
			return await EndlessBookRest.getVoices(getBookHost(getState())!, await BookEffects.getToken());
		} catch (e) {
			console.error('BookEffects.loadVoices failed', e);
			return null;
		}
	}

	public static async getVoiceSample(languageName: string, voiceName: string): Promise<string | null> {
		try {
			return await EndlessBookRest.getVoiceSample(getBookHost(getState())!, await BookEffects.getToken(), languageName, voiceName);
		} catch (e) {
			console.error('BookEffects.getVoiceSample failed', e);
			getDispatch()(setSnackbarPanelAction(true, 'Could not play the sample'));
			return null;
		}
	}

	// Resolves to the user's voice choices by language code, or null if saving failed.
	public static async setVoice(languageCode: string, voiceName: string): Promise<Record<string, string> | null> {
		try {
			return await EndlessBookRest.setVoice(getBookHost(getState())!, await BookEffects.getToken(), languageCode, voiceName);
		} catch (e) {
			console.error('BookEffects.setVoice failed', e);
			getDispatch()(setSnackbarPanelAction(true, 'Could not save the voice'));
			return null;
		}
	}

	// No regenerate: the reader reloads the same pages with the new translation language.
	public static async setTranslationLang(bookId: number, nativeLang: string): Promise<void> {
		const dispatch = getDispatch();
		const settings = getBookSettings(getState(), bookId);
		if (!settings || settings.nativeLang === nativeLang) return;
		try {
			await EndlessBookRest.setTranslationLang(getBookHost(getState())!, await BookEffects.getToken(), bookId, nativeLang);
			dispatch(setBookSettingsAction(bookId, { ...settings, nativeLang }));
			dispatch(bumpBookReloadAction(bookId));
		} catch (e) {
			console.error('BookEffects.setTranslationLang failed', e);
			dispatch(setSnackbarPanelAction(true, 'Could not change the translation language'));
		}
	}

	// New settings → a fresh page 1 under the same book id; the reader then remounts on it.
	public static async regenerate(bookId: number, settings: TBookSettings): Promise<void> {
		const dispatch = getDispatch();
		dispatch(setBookRegeneratingAction(bookId, true));
		try {
			const token = await BookEffects.getToken();
			await EndlessBookRest.regenerate(getBookHost(getState())!, token, bookId, settings);
			dispatch(setBookSettingsAction(bookId, settings));
			dispatch(bumpBookReloadAction(bookId));
		} catch (e) {
			console.error('BookEffects.regenerate failed', e);
			dispatch(setSnackbarPanelAction(true, 'Failed to regenerate the book'));
		} finally {
			dispatch(setBookRegeneratingAction(bookId, false));
		}
	}

	// Called on every page turn too (the book row changes) — only store actual setting changes.
	public static onBookLoaded(book: IBook): void {
		BookEffects.retriedAuth.delete(book.id);
		const settings: TBookSettings = {
			genre: book.genre || '',
			styleAuthor: book.style_author || '',
			plotSeed: book.plot_seed || '',
			nonfiction: !!book.nonfiction,
			keywords: (book.keywords || []).join(', '),
			targetLang: book.target_lang,
			nativeLang: book.native_lang,
		};
		const current = getBookSettings(getState(), book.id);
		if (current && JSON.stringify(current) === JSON.stringify(settings)) return;
		getDispatch()(setBookSettingsAction(book.id, settings));
	}

	public static async onReaderError(audioId: number, bookId: number, error: IBookReaderError): Promise<void> {
		if (error.status === 401) {
			// Token rejected (expired early, secret rotated): remount once with a fresh one. A second
			// 401 means the secrets differ — retrying would just loop, so the reader shows its error.
			if (BookEffects.retriedAuth.has(bookId)) return;
			BookEffects.retriedAuth.add(bookId);
			BookEffects.token = null;
			getDispatch()(bumpBookReloadAction(bookId));
			return;
		}
		if (error.status !== 404) return;
		// deleted on endless-book's side: forget it, the tab falls back to "Create new book"
		try {
			await AudioRest.saveBookId(audioId, null);
			const groupId = RecordingsEffects.findGroupId(audioId);
			if (groupId) getDispatch()(updateAudioAction(audioId, groupId, { bookId: null }));
		} catch (e) {
			console.error('BookEffects.onReaderError: failed to clear the book id', e);
		}
	}

}
